import React from 'react';
import { StanButton, StanCard } from '@/components';
import { createIconElement } from '@/lib';
import IconPlus from '@/assets/icons/ic-plus.svg';
import IconAlertCircle from '@/assets/icons/ic-alert-circle.svg';
import IconSettings from '@/assets/icons/ic-settings.svg';
import type { Integration } from '../../types/integrations.types';

interface IntegrationCardProps {
    integration: Integration;
    loading: boolean;
    onConnect: (integration: Integration) => void;
    onDisconnect: (integration: Integration) => void;
    onSettings?: (integration: Integration) => void;
}

export const IntegrationCard = ({
    integration,
    loading,
    onConnect,
    onDisconnect,
    onSettings,
}: IntegrationCardProps) => {
    const handleDocumentationClick = () => {
        if (integration.documentationUrl) {
            window.open(integration.documentationUrl, '_blank');
        }
    };

    return (
        <StanCard className='gap-1 !p-5'>
            <div className='mb-2 flex items-start justify-between'>
                <div className='flex items-center'>
                    <div className='mr-3 flex size-10 items-center justify-center'>
                        {createIconElement(integration.icon, {
                            removeFill: false,
                        })}
                    </div>
                    <div>
                        <h3>{integration.title}</h3>
                    </div>
                </div>
                {integration.status === 'connected' && (
                    <div className='bg-success-active my-auto ml-1 size-2 rounded-full' />
                )}
            </div>

            <p className='text-heading-h5'>{integration.subtitle}</p>
            <p className='text-caption-1-regular mb-2'>{integration.description}</p>

            {integration.documentationUrl && (
                <StanButton
                    name={`integration-${integration.id}-learn-more-button`}
                    variant='link'
                    size='xs'
                    className='!m-0 !mb-3 !h-auto justify-start !p-0 text-left !font-semibold'
                    onClick={handleDocumentationClick}
                >
                    Learn More
                </StanButton>
            )}

            <div className='mt-auto flex w-full flex-col'>
                {integration.status === 'connected' && (
                    <>
                        {integration.id === 'google-calendar' && (
                            <StanButton
                                name={`integration-${integration.id}-settings-button`}
                                size='sm'
                                className='w-full'
                                onClick={() => onSettings?.(integration)}
                                loading={loading}
                                icon={IconSettings}
                            >
                                Settings
                            </StanButton>
                        )}
                        <StanButton
                            name={`integration-${integration.id}-disconnect-button`}
                            variant='outline'
                            size='sm'
                            className='w-full'
                            onClick={() => onDisconnect(integration)}
                            loading={loading}
                        >
                            Disconnect {integration.identifier || ''}
                        </StanButton>
                    </>
                )}

                {integration.status === 'disconnected' && (
                    <StanButton
                        name={`integration-${integration.id}-connect-button`}
                        size='sm'
                        className='w-full'
                        onClick={() => onConnect(integration)}
                        loading={loading}
                        icon={IconPlus}
                    >
                        Connect
                    </StanButton>
                )}

                {integration.status === 'error' && (
                    <StanButton
                        name={`integration-${integration.id}-reconnect-button`}
                        color='destructive'
                        variant='outline'
                        size='sm'
                        className='w-full'
                        onClick={() => onConnect(integration)}
                        loading={loading}
                        icon={IconAlertCircle}
                    >
                        Reconnect
                    </StanButton>
                )}
            </div>
        </StanCard>
    );
};
